'use client';

import { useEffect, useLayoutEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

/** Supplemental help only. Core facts and warnings remain visible. */
export function InfoTooltip({ title, text, triggerLabel }: { title: string; text: string; triggerLabel?: string }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 12, top: 12, maxHeight: 320 });
  const root = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLSpanElement>(null);
  const pinned = useRef(false);
  const suppressFocus = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const close = () => { cancelClose(); pinned.current = false; setOpen(false); };
  const scheduleClose = () => {
    if (pinned.current || root.current?.contains(document.activeElement)) return;
    cancelClose();
    // Allow the pointer to cross the gap into the hoverable help panel.
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => () => cancelClose(), []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (!root.current || !panel.current) return;
      const anchor = root.current.getBoundingClientRect();
      const margin = 12;
      const gap = 6;
      const below = Math.max(0, window.innerHeight - anchor.bottom - margin - gap);
      const above = Math.max(0, anchor.top - margin - gap);
      const useAbove = above > below && panel.current.scrollHeight > below;
      const maxHeight = Math.min(480, Math.max(above, below));
      const height = Math.min(panel.current.scrollHeight, maxHeight);
      const width = panel.current.getBoundingClientRect().width;
      setPosition({
        left: Math.max(margin, Math.min(anchor.right - width, window.innerWidth - width - margin)),
        top: Math.max(margin, Math.min(useAbove ? anchor.top - gap - height : anchor.bottom + gap, window.innerHeight - height - margin)),
        maxHeight,
      });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open, text]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node) && !panel.current?.contains(event.target as Node)) close();
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') close(); };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);

  return <span ref={root} className="inline-flex shrink-0" onMouseEnter={() => { cancelClose(); setOpen(true); }} onMouseLeave={scheduleClose}>
    <button
      type="button"
      aria-label={`More information: ${title}`}
      aria-expanded={open}
      aria-describedby={open ? id : undefined}
      onPointerDown={() => { suppressFocus.current = true; }}
      onFocus={() => { if (!suppressFocus.current) setOpen(true); }}
      onBlur={() => { suppressFocus.current = false; close(); }}
      onClick={() => { cancelClose(); pinned.current = !pinned.current; setOpen(pinned.current); suppressFocus.current = false; }}
      className={`inline-flex min-h-12 items-center justify-center rounded-control focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus ${triggerLabel ? 'text-left text-content hover:underline underline-offset-4' : 'w-12 text-focus'}`}
    >{triggerLabel ?? <InformationCircleIcon aria-hidden="true" className="h-4 w-4" />}</button>
    {open ? createPortal(<span
      ref={panel}
      id={id}
      role="tooltip"
      onMouseEnter={cancelClose}
      onMouseLeave={scheduleClose}
      style={{ left: position.left, top: position.top, maxHeight: position.maxHeight }}
      className="fixed z-[100] w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto break-words rounded-card border border-line bg-surface p-4 text-left text-sm font-normal leading-6 text-content shadow-e2"
    >{text}</span>, document.body) : null}
  </span>;
}
